import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  selectVoiceSettings,
  setVoiceSettingsAction,
} from "components/suno-dialer/suno-dialer-slice";
import { SUNO_DIALER_FEATURE } from "constants/feature-flags";
import { formatVoiceUserSettingsInput } from "features/administration/users/components/user-tabs/voice-user-settings-tab/voice-user-settings.utils";
import { selectCurrentUser } from "features/auth/auth-slice";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect } from "react";
import {
  useGetUserSettingsQuery,
  useUpdateUserSettingsMutation,
} from "services/suno-comms/suno-comms-api";
import { UserSettingsInput } from "services/suno-comms/types/user-settings.type";

export default function useVoiceUserSettings() {
  const flags = useFlags();
  const isSunoDialerEnabled = flags[SUNO_DIALER_FEATURE];
  const currentUser = useAppSelector(selectCurrentUser);
  const dispatch = useAppDispatch();

  const voiceSettings = useAppSelector(selectVoiceSettings);

  const { data, isSuccess, refetch } = useGetUserSettingsQuery(
    {
      user_oms_id: String(currentUser?.id),
    },
    {
      skip: !isSunoDialerEnabled || !currentUser.id || currentUser.id === -1,
    }
  );

  useEffect(() => {
    if (isSuccess && data) {
      const newValue = {
        id: data.id,
        ...formatVoiceUserSettingsInput(data),
      };
      dispatch(setVoiceSettingsAction(newValue));
    }
  }, [data, dispatch, isSuccess]);

  const [updateUserSettings] = useUpdateUserSettingsMutation();

  const handleChangeSettings = useCallback(
    (data: Partial<UserSettingsInput> & { id?: string }) => {
      const newValue = {
        id: data.id,
        ...formatVoiceUserSettingsInput({
          ...voiceSettings,
          ...data,
        }),
      };

      dispatch(setVoiceSettingsAction(newValue));
      if (newValue.id) {
        updateUserSettings(newValue);
      }
    },
    [dispatch, updateUserSettings, voiceSettings]
  );

  return {
    voiceSettings,
    refetch,
    handleChangeSettings,
  };
}
